import {
  Database_Procs_AdminMoveGetSchoolTeams_Item,
  Entities_Team_UpdateInfo_Request,
  SchoolId,
  TeamId,
} from '@suntex.fim/api-client';
import { $SpecialGradeLevel, SpecialGradeLevel } from '@suntex.fim/enums';

// matches SQL UpdateTeamInfo.sql: @DisplayName NVARCHAR(25), @ClassName NVARCHAR(50)
export const DISPLAY_NAME_MAX_LENGTH = 25;
export const CLASS_NAME_MAX_LENGTH = 50;

export interface TeamEdits {
  displayName: string;
  className: string;
  grade: SpecialGradeLevel;
}

/**
 * builds the edit modal's initial TeamEdits from a raw API row, normalizing
 * null display name/class name (seen in real API responses despite the
 * generated client's string typing) to empty strings so controlled inputs
 * never receive null, and falling back to SpecialGradeLevel.Null when the
 * raw grade doesn't match a known tagged value
 */
export function createTeamEdits(
  team: Database_Procs_AdminMoveGetSchoolTeams_Item,
): TeamEdits {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- API can return null despite the generated client's string typing
    displayName: team.displayName ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- API can return null despite the generated client's string typing
    className: team.className ?? '',
    grade:
      $SpecialGradeLevel.fromOptional(team.grade) ?? SpecialGradeLevel.Null,
  };
}

/**
 * builds the team update request from a source row and the edited fields,
 * preserving fields the edit form doesn't expose (e.g. excludeRankings).
 * teamName (team.name) is immutable and never sent; only displayName
 * (the team leader's login display name) is editable.
 */
export function buildTeamUpdateRequest(
  team: Database_Procs_AdminMoveGetSchoolTeams_Item,
  schoolId: SchoolId,
  edits: TeamEdits,
): Entities_Team_UpdateInfo_Request {
  return {
    teamId: team.teamID as TeamId,
    schoolId,
    displayName: edits.displayName.trim(),
    className: edits.className.trim(),
    excludeRankings: team.excludeRankings,
    grade: edits.grade,
  };
}

/** a team edit is only savable with a non-blank, length-valid display name and class name, and a known grade */
export function isTeamEditValid(edits: TeamEdits): boolean {
  const displayName = edits.displayName.trim();
  const className = edits.className.trim();
  return (
    displayName.length > 0 &&
    displayName.length <= DISPLAY_NAME_MAX_LENGTH &&
    className.length > 0 &&
    className.length <= CLASS_NAME_MAX_LENGTH &&
    edits.grade !== SpecialGradeLevel.Null
  );
}

/**
 * grade table cell label: falls back to the raw numeric grade when it
 * doesn't match a known tagged value. Checks for `undefined` explicitly
 * since valid grade values (e.g. Kindergarten = 0) are falsy.
 */
export function describeGrade(grade: number): string {
  const known = $SpecialGradeLevel.fromOptional(grade);
  return known !== undefined
    ? $SpecialGradeLevel.describe(known).label
    : String(grade);
}
